import * as React from "react";
import { useCallback, useMemo, useState } from "react";
import { createClasses } from "@bloomberg/media-ui-core";
import styles from "./style.module.css";
import TableColumn from "~components/Table/TableColumn";
import Table from "~components/Table";
import TableHeader from "~components/Table/TableHeader";
import TableBody from "~components/Table/TableBody";
import TableRow from "~components/Table/TableRow";
import TableCell from "~components/Table/TableCell";
import type {
  TableColumnDefinition,
  TableColumnSortConfig,
} from "~types/table";
import { LocalizedText } from "~utils/i18n/translate";

const toNum = (value: unknown): number => {
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    const trimmed = value.trim();

    // this is to account for empty fields (ie. open) so that n/a values go to the end
    if (trimmed.toLowerCase() === "n/a") {
      return Number.POSITIVE_INFINITY;
    }
    const number = Number(trimmed.replace(/,/g, ""));

    return Number.isFinite(number) ? number : Number.POSITIVE_INFINITY;
  }

  return Number.POSITIVE_INFINITY;
};

const toMinutesSinceMidnight = (value: unknown): number => {
  if (typeof value !== "string") return NaN;
  const timeParts = value.trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);

  if (!timeParts) return NaN;
  let hours = parseInt(timeParts[1], 10);
  const minutes = parseInt(timeParts[2], 10);
  const ap = timeParts[3].toUpperCase();

  hours = hours % 12;
  if (ap === "PM") hours += 12;

  return hours * 60 + minutes;
};

type ScrollableTableProps<T> = {
  className?: string;
  columns: TableColumnDefinition<T>[];

  onColumnSortClick?: (
    columnKey: keyof T,
    column?: TableColumnDefinition<T>
  ) => void;
  rows: Array<T>;
};

export default function ScrollableTable<T>(props: ScrollableTableProps<T>) {
  const { className, columns, onColumnSortClick, rows } = props;

  const [sortConfig, setSortConfig] = useState<TableColumnSortConfig<T>>({
    key: null,
    direction: null,
  });

  const handleSort = useCallback(
    (columnKey: keyof T) => {
      const currentCol = columns.find((col) => col.key === columnKey);

      // call parent callback
      onColumnSortClick?.(columnKey, currentCol);

      setSortConfig((current) => {
        if (current.key !== columnKey) {
          return { key: columnKey, direction: "asc" };
        }

        if (current.direction === "asc") {
          return { key: columnKey, direction: "desc" };
        }

        if (current.direction === "desc") {
          return { key: columnKey, direction: "asc" };
        }

        return { key: null, direction: null };
      });
    },
    [columns, onColumnSortClick]
  );

  const sortedRows = useMemo(() => {
    const { key, direction } = sortConfig;

    if (!key || !direction) return rows;

    const currentCol = columns.find((col) => col.key === key);
    const columnType = currentCol?.options?.columnType;

    // use backup key when the main key might not exist in the data
    // e.g. municipalYieldChange1Day might not exist, so use priceChange1Day instead
    const columnKey = (currentCol?.options?.backupKey as keyof T) ?? key;

    return [...rows].sort((a, b) => {
      const aVal = a[columnKey];
      const bVal = b[columnKey];

      if (aVal === null) return 1;
      if (bVal === null) return -1;
      let compare: number;

      if (columnType === "number") {
        const aNum = toNum(aVal);
        const bNum = toNum(bVal);

        compare = aNum - bNum;
      } else if (columnType === "time") {
        const aMin = toMinutesSinceMidnight(aVal);
        const bMin = toMinutesSinceMidnight(bVal);

        compare = aMin - bMin;
      } else if (columnType === "currency") {
        const aCurr = a["id" as keyof T];
        const bCurr = b["id" as keyof T];

        compare = String(aCurr).localeCompare(String(bCurr));
      } else if (typeof aVal === "number" && typeof bVal === "number") {
        compare = aVal - bVal;
      } else {
        compare = String(aVal).localeCompare(String(bVal));
      }

      return direction === "asc" ? compare : -compare;
    });
  }, [columns, rows, sortConfig]);

  const tableClass = createClasses([styles.table, className]);

  return (
    <div className={styles.scrollableTable}>
      <Table className={tableClass}>
        <TableHeader className={styles.header}>
          {columns.map((column, index) => (
            <TableColumn
              key={column.key}
              column={column}
              className={createClasses([
                styles.column,
                { [styles.stickyCol]: index === 0 },
              ])}
              sortState={
                sortConfig.key === column.key ? sortConfig.direction : null
              }
              onSort={
                column.allowSorting ? () => handleSort(column.key) : undefined
              }
            >
              <LocalizedText value={column.title} />{" "}
            </TableColumn>
          ))}
        </TableHeader>
        <TableBody>
          {sortedRows.map((row, rowIndex) => (
            <TableRow
              key={rowIndex}
              rowData={row}
              columns={columns}
              className={styles.row}
            >
              {columns.map((column, index) => (
                <TableCell
                  key={column.key}
                  rowData={row}
                  column={column}
                  className={index === 0 ? styles.stickyCol : undefined}
                />
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
